import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import Modal from 'antd/lib/modal';
import {Field, Form, Formik, FormikProps} from 'formik';
import * as Yup from 'yup';

import {FileInterface, FileType} from '@common/typescript/objects/FileInterface';
import {List} from '@common/typescript/objects/List';

import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import SearchFieldAuto from '@app/components/Forms/SearchField/SearchFieldAuto';
import {ListOption} from '@app/objects/SidebarProps';
import {rest} from '@app/components/Api';
import CustomCheckbox from '@app/components/Forms/CustomCheckbox/CustomCheckbox';
import {stringBeautifyValidator} from '@app/components/LocalCommon/Utils/Validators';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import {onFileUpload} from '@app/components/LocalCommon/Utils/onFileUpload';
import CustomSelect from '@app/components/Forms/CustomSelect/CustomSelect';
import {GAeventSubmit} from '@app/components/LocalCommon/Utils/GAevents';
import {StatesListItem} from '@app/objects/StatesListItem';
import {transformStateList, transformVetList} from '@app/components/LocalCommon/Utils/transformList';
import {Veterinarian} from '@app/objects/Veterinarian';
import FileUpload from '@app/components/LocalCommon/FileUpload/FileUpload';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';

import '@app/scss/localCommon/components/attachment.scss';
import '@app/scss/components/vetTesModal.scss';
import VetTesPreviewModal from '@app/components/Pages/VetTestimonials/VetTesPreviewModal';
import {stateFilter} from '@app/components/LocalCommon/Utils/StateFilter';

interface VetTesModalProps {
	modalVisible: boolean;
	setModalVisible: (e: boolean) => void;
	sent: boolean;
	setSent: (e: boolean) => void;
}

type FormModel = {};

const validationSchema = Yup.object({
	firstName: stringBeautifyValidator('First Name is a required field'),
	city: stringBeautifyValidator('City is a required field'),
	state: stringBeautifyValidator('State is a required field'),
	vetId: stringBeautifyValidator('Veterinarian is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const VetTesModal: React.FC<VetTesModalProps> = ({
	modalVisible,
	setModalVisible,
	sent,
	setSent,
	 }) => {

	const [isChecked, setIsChecked] = React.useState(true);
	const [attachment, setAttachment] = React.useState<FileInterface[]>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'png'];
	const maxImgCount = 1;
	const [stateFormlabel, setStateFormLabel] = React.useState<string | number | undefined>('');
	const [sending, setSending] = React.useState(false);
	const [stateSelected, setStateSelected] = React.useState<string>('');
	const [stateList, setStateList] = React.useState<StatesListItem[]>([]);
	const stateListOptions: ListOption[] = [];
	const vetListOptions: ListOption[] = [];
	const [vetIdList, setVetIdList] = React.useState<Veterinarian[]>([]);
	const [vetFieldValue, setVetFieldValue] = React.useState<string>('');
	const [open, setOpen] = React.useState<boolean>(false);
	const [modalPreviewVisible, setModalPreviewVisible] = React.useState<boolean>(false);
	const [formValues, setFormValues] = React.useState({});
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);
	const openModal = (e) => {
		e.preventDefault();
		setModalPreviewVisible(true);
	};

	const handleOnChange = (e) => {
		let name, value;

		if (e.target) {
			name = e.target.name;
			value = e.target.value;
		} else {
			name = e.name;
			value = e.value;
		}
		setFormValues(prevState => ({
			...prevState,
			[name]: value
		}));
	};

	React.useEffect(() => {
		if (attachment[0]) {
			setFormValues(prevState => ({
				...prevState,
				imgSrc: attachment[0].src
			}));
		}
	}, [attachment]);

	React.useEffect(() => {
		setFormValues(prevState => ({
			...prevState,
			isShowName: isChecked
		}));
	}, [isChecked]);

	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states').then(res => setStateList(res.list));
	}, []);

	React.useEffect(() => {
		const timeout = setTimeout(() => {
			rest.get<List<Veterinarian>>(`v1/veterinarians/?name=${vetFieldValue}`).then((res) => {
				const vetIdListSorted = res.list.sort((a, b) => a.firstName > b.firstName ? 1 : -1);
				setVetIdList(vetIdListSorted);
			});
		}, 500);

		return () => clearTimeout(timeout);
	}, [vetFieldValue]);

	transformStateList(stateList, stateListOptions);
	transformVetList(vetIdList, vetListOptions);

	return (
		<>
			<Modal
				wrapClassName="modal-wrap vet-tes-modal"
				visible={modalVisible}
				onOk={() => setModalVisible(false)}
				onCancel={() => setModalVisible(false)}
				width={1037}
				centered
			>
				<div className="vet-tes-modal__content">
					<div className="modal-wrap__container">
						{
							!sent ?
								<>
									<h3 className="vet-tes-modal-title">
										Submit a testimonial
									</h3>
									<h4 className="vet-tes-modal-subtitle vet-tes-modal-subtitle_desktop">
										Write a testimonial to the loving and compassionate veterinarian
										who helped your pet peacefully cross the Rainbow Bridge.
									</h4>
								</>

							:
								<>
									<h3 className="vet-tes-modal-title vet-tes-modal-title_success">
										Thank you for submitting your testimonial
									</h3>
									<h4 className="vet-tes-modal-subtitle">
										We will post it within 5 business days and you will receive an email letting you know
										the testimonial for your Lap of Love veterinarian is ready to view.
									</h4>
								</>

						}
						{!sent ? <>
							<h4 className="vet-tes-modal__form-title">
								Personal information
							</h4>
							<Formik
								onSubmit={(values, formikActions) => {
									if (isConfirmed) {
										setSending(true);
										rest.post('v1/testimonials', values)
											.then((response) => {
												setSent(true);
												setAttachment([]);
												formikActions.resetForm();
												setStateFormLabel('');
												GAeventSubmit('Testimonial-Form', 'Submit', 'Testimonial-Form');
												setSending(false);
											});
									}
								}}
								initialValues={{
									firstName: '',
									lastName: '',
									isDisplayed: true,
									city: '',
									state: '',
									email: '',
									vetId: '',
									message: '',
									filePath: [],
									captcha: ''
								}}
								validationSchema={validationSchema}
								render={(formikBag: FormikProps<any>) => {
									return (
										<Form className="vet-tes-modal__form">
											<CustomInput
												type="text"
												name="firstName"
												placeholder="First Name*"
												formikBag={formikBag}
												// onBlur={handleOnBlur}
												handleOnChange={handleOnChange}
											/>
											<CustomInput
												type="text"
												name="lastName"
												placeholder="Last Name"
												formikBag={formikBag}
												// onBlur={handleOnBlur}
												handleOnChange={handleOnChange}
											/>
											<CustomCheckbox
												isChecked={isChecked}
												setIsChecked={setIsChecked}
												formikBag={formikBag}
												capture="Would you like your name displayed with your testimonial?"
											/>
											<CustomInput
												type="text"
												name="city"
												placeholder="City*"
												formikBag={formikBag}
												// onBlur={handleOnBlur}
												handleOnChange={handleOnChange}
											/>
											<SearchFieldAuto
												name="state"
												placeholder="State*"
												listOptions={stateListOptions}
												formikBag={formikBag}
												onSelect={(value, option) => {
													formikBag.setFieldValue('state', value);
													setStateFormLabel(option.label);
													setStateSelected(value);
												}}
												label={stateFormlabel}
												onChange={(value, option) => {
													formikBag.setFieldValue('state', value);
													setStateFormLabel(value);
													!value && setStateSelected('');
													handleOnChange({name: 'state', value: option.value});
												}}
												searchButton={false}
												adjustValue
												setStateFormLabel={setStateFormLabel}
												allowClear={true}
												customFilter={stateFilter}
											/>
											{
												!formikBag.values['state'] ? <div className={'break'}/> : null
											}
											<CustomInput
												name="email"
												placeholder="Email Address*"
												formikBag={formikBag}
												removeSpace={true}
												handleOnChange={handleOnChange}
											/>
											<CustomSelect
												name="vetId"
												placeholder="Select or type Veterinarian Name*"
												onSelect={(value, option) => {
													setOpen(false);
													formikBag.setFieldValue('vetId', value);
												}}
												onChange={(value, option) => {
													setOpen(false);
													formikBag.setFieldValue('vetId', value);
													handleOnChange({name: 'vetName', value: option.label});
												}}
												onSearch={(value, option) => {
													value.length > 0 ? setOpen(true) : setOpen(false);
													setVetFieldValue && setVetFieldValue(value);
												}}
												options={vetListOptions}
												showSearch={true}
												onFocus={() => {
												}}
												transform={true}
												formikBag={formikBag}
												parentContainer={'.vet-tes-modal__form'}
												value={!formikBag.values['vetId'] ? undefined : formikBag.values['vetId']}
												showArrow={false}
												open={open}
											/>
											<div className="vet-tes-modal__form-textarea">
												<CustomInput
													component={'textarea'}
													name="message"
													placeholder="Tell us about your experience"
													formikBag={formikBag}
													// onBlur={handleOnBlur}
													handleOnChange={handleOnChange}
												/>
											</div>
											<Field
												name="filePath"
												render={(field) => {
													return (
														<div className={field.form.errors['filePath'] && field.form.touched['filePath'] ? 'attachment attachment_cropped' : 'attachment'}>
															<h4 className="attachment-title">
																Upload picture of your pet
															</h4>
															<AttachmentPreview
																attachment={attachment}
																setAttachment={setAttachment}
																formikBag={formikBag}
															/>
															<div className="attachment-upload-container">
																<FileUpload
																	update={data => onFileUpload(
																		data,
																		field,
																		formikBag,
																		'filePath',
																		maxFile,
																		imgExtension,
																		attachment,
																		maxImgCount,
																		setAttachment,
																	)}
																	type="jobInquiry"
																	fileType={FileType.File}
																	noRelation={true}
																	asButton buttonClassName="btn btn-secondary"
																	buttonCaption="Upload a file"
																	multiple={false}
																	aspectRatio={2 / 3}
																	maxFile={maxFile}
																	field={field}
																/>
																<span className="attachment-note">(Maximum file size is 10MB in JPG or PNG format)</span>
															</div>
															{
																field.form.errors['filePath'] && field.form.touched['filePath'] ?
																	<div
																		className="custom-input__error error_top-gap"> {field.form.errors['filePath']}</div> : null
															}
														</div>
													);
												}}
											/>
											<FormikField
												containerClassName="captcha"
												fieldName="captcha"
												render={fieldProps =>
													<ReCAPTCHA
														sitekey="6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU"
														onChange={(value) => {
															if (value) {
																formikBag.setFieldValue('captcha', value);
																setIsConfirmed(true);
															} else {
																formikBag.setFieldValue('captcha', '');
																setIsConfirmed(false);
																setSending(false);
															}
														}}
														onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
														hl="en"
													/>
												}
											/>
											<div className="modal-button">
												<button
													type="submit"
													className="btn btn-primary btn-lg"
													disabled={sending}
												>
													Submit your testimonial
												</button>
												<button
													type="button"
													className="btn btn-primary btn-lg"
													onClick={openModal}
												>
													Preview your testimonial
												</button>
												{
													sending ?
														<div className="lds-ring">
															<div/>
															<div/>
															<div/>
															<div/>
														</div>
														: null
												}
											</div>
										</Form>
									);
								}}
							/>
						</> : <></>
						}
					</div>
				</div>
			</Modal>
			<VetTesPreviewModal
				modalVisible={modalPreviewVisible}
				setModalVisible = {setModalPreviewVisible}
				tes={formValues}
			/>
		</>
	);
};

export default VetTesModal;